import {
  CHALLENGE_SET_ENROLLMENT,
  CHALLENGE_SET_STATUS,
  CHALLENGE_ACTIVATE,
  CHALLENGE_APPLY_DAY_RESULT,
  CHALLENGE_SET_FINAL_ASSESSMENT_DONE,
  CHALLENGE_RESET,
  CHALLENGE_SET_FROM_SERVER,
} from "../constants/Challenge";
import { CHALLENGE_RULES, CHALLENGE_STATUS } from "constants/ChallengeConfig";
import {
  countValidDays,
  computeCurrentStreak,
  countFreezesUsed,
  isLessonsComplete,
  isChallengeWindowClosed,
} from "utils/challengeHelpers";

export const initState = {
  status: CHALLENGE_STATUS.NONE,
  enrollmentId: null,
  timezone: null,
  reminderTime: null,
  deposit: null,
  startDate: null,
  days: [],
  currentStreak: 0,
  daysSubmitted: 0,
  freezesRemaining: CHALLENGE_RULES.FREEZES_GRANTED,
  finalAssessmentDone: false,
};

/** Recomputes the derived, day-grid-dependent fields of the state. */
const withDerivedFields = (state, days) => {
  const daysSubmitted = countValidDays(days);
  const freezesRemaining = CHALLENGE_RULES.FREEZES_GRANTED - countFreezesUsed(days);
  const currentStreak = computeCurrentStreak(days);
  const lessonsComplete = isLessonsComplete(days);
  const windowClosed = isChallengeWindowClosed(days);

  let status = state.status;
  if (lessonsComplete) {
    status = CHALLENGE_STATUS.LESSONS_COMPLETED;
  } else if (windowClosed) {
    status = CHALLENGE_STATUS.CHALLENGE_INCOMPLETE;
  }

  return {
    ...state,
    days,
    daysSubmitted,
    freezesRemaining,
    currentStreak,
    status,
  };
};

/**
 * Maps the server's challenge state onto the shape the screens read.
 * The grid's "today" marker is derived here because the API reports the
 * current day separately from each day's stored status.
 */
const fromServer = (state, payload) => {
  const enrollment = payload.enrollment;
  if (!enrollment) {
    return { ...initState, freezesRemaining: payload.freezes_remaining ?? initState.freezesRemaining };
  }

  const days = (payload.days || []).map((day) => ({
    dayIndex: day.day_index,
    status:
      day.day_index === payload.current_day_index && day.status === "pending"
        ? "today"
        : day.status,
    lessonCode: day.lesson_code,
    uploadItemId: day.upload_item_id,
    submittedAt: day.submitted_at,
  }));

  return {
    ...state,
    status: enrollment.status,
    enrollmentId: enrollment.id,
    timezone: enrollment.timezone,
    reminderTime: enrollment.reminder_time,
    deposit: {
      amount: enrollment.deposit_amount,
      currency: enrollment.deposit_currency,
      symbol: enrollment.deposit_currency === "USD" ? "$" : "₹",
    },
    startDate: enrollment.start_date,
    days,
    currentStreak: payload.current_streak || 0,
    daysSubmitted: payload.days_submitted || 0,
    freezesRemaining: payload.freezes_remaining || 0,
    // The server reports this directly. It used to be inferred from
    // `assessment_unlocked`, which means "the final assessment is available",
    // not "it was taken" — the two are near-opposites once the challenge
    // closes out.
    finalAssessmentDone: Boolean(payload.final_assessment_done),
    todayLessonCode: payload.today_lesson_code || null,
    currentDayIndex: payload.current_day_index || null,
  };
};

const challenge = (state = initState, action) => {
  switch (action.type) {
    case CHALLENGE_SET_FROM_SERVER:
      return fromServer(state, action.payload);
    case CHALLENGE_SET_ENROLLMENT:
      return {
        ...state,
        enrollmentId: action.payload.enrollmentId,
        timezone: action.payload.timezone,
        reminderTime: action.payload.reminderTime,
        deposit: action.payload.deposit,
        status: action.payload.status,
      };
    case CHALLENGE_SET_STATUS:
      return { ...state, status: action.payload };
    case CHALLENGE_ACTIVATE:
      return withDerivedFields(
        { ...state, startDate: action.payload.startDate, status: CHALLENGE_STATUS.ACTIVE },
        action.payload.days
      );
    case CHALLENGE_APPLY_DAY_RESULT:
      return withDerivedFields(state, action.payload);
    case CHALLENGE_SET_FINAL_ASSESSMENT_DONE:
      return { ...state, finalAssessmentDone: true };
    case CHALLENGE_RESET:
      return initState;
    default:
      return state;
  }
};

export default challenge;
